import { useState } from 'react';
import {
  line, Color, LIST_PAGES, NEW_DOCUMENT_ID, Size, taxJurisdictionRowLead, creditNotePills, pageKindLineText, t, type CreditNoteAnswer,
} from '@bms2/shared';
import { Button, DatePicker, NumericInput, Select, TextInput } from '../../components/controls';
import { BANK_ACCOUNTS_PATH } from '../banking/api';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Money } from '../../components/Money';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { WhatsNextBanner } from '../../components/composites/WhatsNextBanner';
import { Modal } from '../../components/modals/Modal';
import { ReasonWindow } from '../../components/modals/ReasonWindow';
import modalStyles from '../../components/modals/modal.module.css';
import { AccessDenied } from '../../shell/Fallbacks';
import { useStatusResolver } from '../../shell/StatusResolver';
import { CreditNoteForm } from './CreditNoteForm';
import { BannerPills } from './BannerPills';
import { DocumentFilesSection } from './DocumentFilesSection';
import { DocumentViewPage, type DocumentLinesDescriptor } from './DocumentViewPage';
import { CREDIT_NOTES_PATH, post, useApi, useRows } from './api';
import { documentPdfPath } from './ViewPdfLink';
import { type ScreenFact } from './PageFacts';
import styles from './DocumentPage.module.css';
import { DealTeamPanel } from './DealTeamPanel';
import { useDocumentToday, useViewerZone } from '../../shell/ViewerZone';
import { DocumentNotes, carriedNotes } from './DocumentNotes';
import { AccountJournalTile } from '../accounting/AccountJournalTile';

type OpenModal = 'receive' | 'void' | 'refundHeld' | null;

/** The rails a payment carries; the labels are the enum's own words, as the payment form draws them. */
const METHODS = ['Check', 'ACH', 'Card', 'Cash', 'Other'] as const;

/**
 * 🔒 The Credit Note's page (piece 22; decision 0409): view mode draws Regions
 * 1 to 10 over the projection the server built, then the credited lines
 * beneath; edit mode is the form the `+ New` route opens.
 *
 * 🔨 The page carries no Issue control, because a credit note is issued when it
 * is raised: one that restocks opens at Pending return and one that does not
 * opens Issued. Receiving the goods is what applies the first kind.
 */
export function CreditNotePage({ noteId, apiBaseUrl = '' }: { noteId: string; apiBaseUrl?: string }) {
  // 🔒 The banner's pills read the shell's one status resolver, as the lists do (decision 0639).
  const resolveStatus = useStatusResolver();
  /**
   * 🔨 `documentPageMode` is not asked here. It answers edit to any address
   * carrying `?mode=edit`, and the form this page holds takes no record id, so
   * that answer would draw an empty new-note form over a saved note and raise a
   * second credit note on save (found in review, 2026-09-15). A credit note has
   * no edit mode at all, so the sentinel id is the whole question.
   */
  const creating = noteId === NEW_DOCUMENT_ID;
  const page = useApi<CreditNoteAnswer>(creating ? null : `${CREDIT_NOTES_PATH}/${noteId}`, apiBaseUrl);
  const zone = useViewerZone();
  const sites = useRows<{ id: string; label: string }>(creating ? null : `${CREDIT_NOTES_PATH}/receiving-sites`, apiBaseUrl);
  const [modal, setModal] = useState<OpenModal>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [siteId, setSiteId] = useState('');
  // 🔒 Refund the Held Credit (decision 2100): what to pay back, how, on which day, and from which account.
  const today = useDocumentToday();
  const [refundCents, setRefundCents] = useState<number | null>(null);
  const [refundMethod, setRefundMethod] = useState<string>('Check');
  const [refundDate, setRefundDate] = useState(today);
  const [refundReference, setRefundReference] = useState('');
  const [refundAccountId, setRefundAccountId] = useState('');
  const bankAccounts = useRows<{ id: string; name: string; isActive: boolean }>(modal === 'refundHeld' ? BANK_ACCOUNTS_PATH : null, apiBaseUrl);

  const close = () => setModal(null);

  // 🔨 A credit note is created on its own page and never edited afterwards, so a saved note draws its record whatever the address asks for.
  if (creating) return <CreditNoteForm apiBaseUrl={apiBaseUrl} />;
  if (page.denied) return <AccessDenied reason={page.deniedReason} />;

  const act = async (path: string, body: unknown, ok: string) => {
    setBusy(true);
    const answer = await post<unknown>(`${CREDIT_NOTES_PATH}/${noteId}/${path}`, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      setNotice(`!${answer.message}`);
      return false;
    }
    close();
    setNotice(ok);
    page.reload();
    return true;
  };

  const siteRows = sites.state.kind === 'ready' ? sites.state.data : [];

  return (
    <AsyncStatePanel state={page.state} emptyAction={t('document.notFound')} onRetry={page.reload}>
      {(note) => {
        const facts: ScreenFact[] = [
          { label: t('creditNote.fact.account'), value: note.companyName, route: `/companies/${note.companyId}` },
          { label: t('creditNote.fact.invoice'), value: note.sourceInvoiceNumber, route: `/invoices/${note.sourceInvoiceId}` },
          { label: t('creditNote.fact.creditDate'), value: note.creditDate },
          { label: t('creditNote.fact.reason'), value: note.reason },
          { label: t('creditNote.fact.applied'), value: <Money cents={note.appliedCents} currencyCode={note.currencyCode} /> },
          // 🔒 The share the invoice could not take is named, and where it went: the account's available credit (decision 1701). Nothing is drawn where the invoice took it all.
          // 🔒 What a refund payment paid back of the held credit (decision 2100); nothing is drawn where none was refunded.
          { label: t('creditNote.fact.refunded'), value: (note.refundedCents ?? 0) > 0 ? <span data-credit-note-refunded={note.refundedCents}><Money cents={note.refundedCents ?? 0} currencyCode={note.currencyCode} /></span> : null },
          { label: t('creditNote.fact.heldAsCredit'), value: (note.heldAsCreditCents ?? 0) > 0 ? <span data-credit-note-held={note.heldAsCreditCents}><Money cents={note.heldAsCreditCents ?? 0} currencyCode={note.currencyCode} /></span> : null, route: (note.heldAsCreditCents ?? 0) > 0 ? `/companies/${note.companyId}` : null },
        ];
        const footer = (
          <>
            {note.footer?.mayReceive ? <Button size={Size.Medium} color={Color.Navy} text={[t('creditNote.action.receive')]} actions={[() => setModal('receive')]} disabled={busy} /> : null}
            {/* 🔒 Held credit has a way out (decision 2100): paid back as a payment sent to the account against this note. */}
            {note.footer?.mayRefundHeld ? <Button size={Size.Medium} color={Color.Navy} text={[t('creditNote.action.refundHeld')]} actions={[() => { setRefundCents(note.heldAsCreditCents ?? null); setRefundDate(today); setModal('refundHeld'); }]} disabled={busy} /> : null}
            {note.footer?.mayVoid ? <Button size={Size.Medium} color={Color.Inactive} text={[t('creditNote.action.void')]} actions={[() => setModal('void')]} disabled={busy} /> : null}
            {note.footer?.refusal ? <span className={styles.refusal} data-credit-note-refusal="true">{note.footer.refusal}</span> : null}
            {/* 🔨 The sheet's own control moved to the action bar above both views, so one document offers it once (decision 0612). */}
          </>
        );
        const moneyOf = (amount: number) => <Money cents={amount} currencyCode={note.currencyCode} />;
        /** 🔒 The credited lines as the one read-only table draws them (decision 0612). */
        const lines: DocumentLinesDescriptor = {
          title: t('creditNote.lines.title'),
          rowAttribute: 'data-credit-note-line',
          blockAttribute: 'data-credit-note-lines',
          rows: (note.lines ?? []).map((row) => ({
            id: row.id,
            label: row.description,
            note: row.returnable ? null : t('creditNote.lines.notReturnable'),
            cells: [String(row.quantity), row.unitPriceCents === null ? '' : moneyOf(row.unitPriceCents)],
            amount: moneyOf(row.amountCents),
          })),
          // 🔨 The line reads net and the tax totals once beneath it, so the block foots the way the sheet above it does.
          totals: [
            // 🔒 One Tax line per jurisdiction the note stored, its invoice's own (decision 0738); an older note draws its one.
            ...((note.taxRows ?? []).length > 0
              ? (note.taxRows ?? []).map((row) => ({ key: `tax-${row.level}-${row.label}`, label: <>{taxJurisdictionRowLead(row)} {moneyOf(row.taxableBaseCents)}</>, value: moneyOf(row.taxCents), attributes: { 'data-credit-note-tax': String(row.taxCents), 'data-tax-level': row.level } }))
              : note.taxCents > 0 ? [{ label: t('creditNote.lines.tax'), value: moneyOf(note.taxCents), attributes: { 'data-credit-note-tax': String(note.taxCents) } }] : []),
            { label: t('creditNote.lines.total'), value: moneyOf(note.totalCents), attributes: { 'data-credit-note-total': String(note.totalCents) } },
          ],
        };
        // 🔒 One notes panel, placed in the Details view and handed to the PDF view's rail; only one view is mounted (T587; decision 1403).
        const carried = carriedNotes(note);
        const notesPanel = <DocumentNotes recordType="CreditNote" recordId={note.id} recordLabel={note.creditNoteNumber} apiBaseUrl={apiBaseUrl} onError={(message) => setNotice(`!${message}`)} carried={carried} />;
        // 🔒 The files attached to this CreditNote, one block placed in the Details view (decision 0457; T1071).
        const filesPanel = <DocumentFilesSection recordType="CreditNote" recordId={note.id} writeRefusal={note.writeRefusal ?? null} apiBaseUrl={apiBaseUrl} />;
        // One Account Journal tile, placed in the Details view and handed to the PDF view's rail as its Journal tab (T1070; decision 1662).
        const journalPanel = <AccountJournalTile sourceDocumentType="CreditNote" sourceDocumentId={note.id} apiBaseUrl={apiBaseUrl} />;
        return (
          <DocumentViewPage
            notes={carried === null ? null : notesPanel}
            files={filesPanel}
            journal={journalPanel}
            documentType="CreditNote"
            icon="retry"
            title={note.creditNoteNumber}
            subtitle={pageKindLineText({ page: 'document', documentType: 'CreditNote' })}
            information={facts}
            status={<BannerPills pills={creditNotePills(note, resolveStatus)} attribute="data-credit-note-pills" />}
            footer={footer}
            footerAttributes={{ 'data-credit-note-actions': note.footer?.mayReceive ? 'receive' : 'none' }}
            pageAttributes={{ 'data-credit-note': note.id, 'data-credit-note-status': note.status }}
            print={note.printDocument ?? null}
            layout={note.printLayout ?? null}
            sheet={note.printSheet ?? null}
            writeRefusal={note.writeRefusal ?? null}
            ribbon={note.status}
            pdfPath={documentPdfPath(CREDIT_NOTES_PATH, note.id)}
            listRoute={LIST_PAGES.CreditNote.route}
            apiBaseUrl={apiBaseUrl}
            terms={{ documentId: note.id, onSaved: page.reload }}
            lines={lines}
            details={
              <>
                {/* 🔒 What this document posted and where each amount was placed (decision 1075). */}
                {journalPanel}
                <DealTeamPanel team={note.dealTeam} timeZone={zone} />
                {filesPanel}
                {/* 🔒 The document's notes, the composer at the top of the list (decision 0951; T336). */}
                {notesPanel}
              </>
            }
            whatsNext={
              /* 🔒 The footer's own moves, named as the next step (decision 1732). */
              <WhatsNextBanner documentType="CreditNote" status={note.status} disabled={busy} actions={note.footer?.mayReceive ? { receiveReturn: () => setModal('receive') } : {}} />
            }
            notices={
              <>
                {notice ? <AlertBanner tone={notice.startsWith('!') ? 'error' : 'good'} message={notice.replace(/^!/, '')} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} /> : null}
                {note.bypassReason ? <p className={styles.sentence} data-credit-note-bypass>{line(t('creditNote.new.bypass'), '·', note.bypassReason)}</p> : null}
              </>
            }
          >
              {modal === 'receive' ? (
                <Modal
                  open
                  title={t('creditNote.receive.title')}
                  onCancel={close}
                  dataModal="credit-note-receive"
                  actions={[
                    { kind: 'primary', label: t('creditNote.action.receive'), onAct: () => void act('receive', { siteId: siteId || null }, t('creditNote.received')), disabled: busy },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  <p className={modalStyles.fieldLabel}>{t('creditNote.receive.hint')}</p>
                  <Select size={Size.Medium} label={t('creditNote.receive.site')} value={siteId} onChange={setSiteId} placeholder={t('creditNote.receive.sitePlaceholder')} options={siteRows.map((site) => ({ value: site.id, label: site.label }))} dataAttribute={{ 'data-credit-note-site': 'true' }} />
                </Modal>
              ) : null}
              {modal === 'refundHeld' ? (
                <Modal
                  open
                  title={t('creditNote.refundHeld.title')}
                  onCancel={close}
                  dataModal="credit-note-refund-held"
                  actions={[
                    {
                      kind: 'primary', label: t('creditNote.action.refundHeld'), disabled: busy || !refundCents || refundCents <= 0,
                      onAct: () => void act('refund-held', { amountCents: refundCents, method: refundMethod, paymentDate: refundDate || null, reference: refundReference.trim() || null, bankAccountId: refundAccountId || null }, t('creditNote.refundHeld.done')),
                    },
                    { kind: 'cancel', label: t('modal.cancel'), onAct: close },
                  ]}
                >
                  <p className={modalStyles.fieldLabel}>{t('creditNote.refundHeld.hint')}</p>
                  <NumericInput size={Size.Medium} label={t('creditNote.refundHeld.amount')} kind="currency" value={refundCents} onChange={setRefundCents} required name="refundHeldAmount" />
                  <Select size={Size.Medium} label={t('creditNote.refundHeld.method')} value={refundMethod} onChange={setRefundMethod} options={METHODS.map((row) => ({ value: row, label: row }))} required />
                  <DatePicker size={Size.Small} label={t('creditNote.refundHeld.date')} value={refundDate} onChange={setRefundDate} mode="date" name="refundHeldDate" />
                  <TextInput size={Size.Medium} label={t('creditNote.refundHeld.reference')} value={refundReference} onChange={setRefundReference} name="refundHeldReference" />
                  <Select size={Size.Medium} label={t('creditNote.refundHeld.paidFrom')} value={refundAccountId} onChange={setRefundAccountId} placeholder={t('creditNote.refundHeld.paidFromNone')} options={(bankAccounts.state.kind === 'ready' ? bankAccounts.state.data : []).filter((row) => row.isActive).map((row) => ({ value: row.id, label: row.name }))} />
                </Modal>
              ) : null}
              {modal === 'void' ? (
                <ReasonWindow
                  open
                  title={t('creditNote.void.title')}
                  label={t('creditNote.void.reason')}
                  actLabel={t('creditNote.action.void')}
                  busy={busy}
                  onCancel={close}
                  refusal={notice?.startsWith('!') ? notice.slice(1) : null}
                  dataModal="credit-note-void"
                  onAct={(reason) => act('void', { reason }, t('creditNote.voided'))}
                />
              ) : null}
          </DocumentViewPage>
        );
      }}
    </AsyncStatePanel>
  );
}
